import React, { useState } from "react";
import { useGeofence } from "@/contexts/GeofenceContext";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, Navigation, Loader2, CheckCircle2, AlertTriangle, Settings2, Crosshair } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function LocationStatusPill() {
    const { status, distanceMeters, societyConfig, checkLocation, checking, coords, refreshConfig } = useGeofence();
    const { user } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [radiusInput, setRadiusInput] = useState(societyConfig.radius_meters || 650);

    const isAdmin = user?.role === "admin";

    const formatDist = (meters) => {
        if (meters == null) return null;
        if (meters < 1000) return `${meters}m`;
        return `${(meters / 1000).toFixed(1)}km`;
    };

    // Admin calibration: lock current GPS as gate coordinates
    const handleCalibrateHere = async () => {
        if (!coords) {
            toast.error("Current GPS location not acquired yet. Please tap Re-check GPS first.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/society/geofence", {
                society_name: societyConfig.society_name,
                latitude: coords.latitude,
                longitude: coords.longitude,
                radius_meters: Number(radiusInput) || 650,
                guard_strict: true,
                resident_warning: true,
                enabled: true,
            });
            toast.success("Society gate coordinates calibrated to current GPS position!");
            await refreshConfig();
            checkLocation();
            setShowModal(false);
        } catch (err) {
            toast.error("Failed to update geofence coordinates");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveRadius = async () => {
        setSaving(true);
        try {
            await api.post("/society/geofence", {
                ...societyConfig,
                radius_meters: Number(radiusInput) || 650,
            });
            toast.success("Geofence perimeter radius updated!");
            await refreshConfig();
            checkLocation();
            setShowModal(false);
        } catch (err) {
            toast.error("Failed to update radius");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setShowModal(true)}
                className={`hidden md:inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg border transition cursor-pointer shrink-0 ${
                    status === "inside"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                        : status === "outside"
                        ? "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
                        : "bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200"
                }`}
                title="Click to view society location & geofence details"
            >
                <MapPin size={12} className={status === "inside" ? "text-emerald-600" : "text-amber-600"} />
                <span>
                    {checking
                        ? "Locating…"
                        : status === "inside"
                        ? "Pyramid City 3"
                        : status === "outside"
                        ? `Remote (${formatDist(distanceMeters)})`
                        : "Location Off"}
                </span>
            </button>

            {/* Location & Geofence Details Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white w-full max-w-md rounded-2xl border border-nest-line shadow-2xl p-5 space-y-4">
                        <div className="flex items-center justify-between pb-3 border-b border-nest-line">
                            <div className="flex items-center gap-2">
                                <div className="p-2 rounded-xl bg-nest-base border border-nest-line text-nest-ink">
                                    <MapPin size={18} className="text-nest-clay" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm text-nest-ink">Location & Geofencing</h3>
                                    <p className="text-[11px] text-nest-ink3">{societyConfig.society_name}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="text-nest-ink3 hover:text-nest-ink text-sm p-1 rounded-lg cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Location status overview */}
                        <div className="rounded-xl p-3.5 bg-nest-base/60 border border-nest-line space-y-2">
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-nest-ink3">Current Status:</span>
                                <span
                                    className={`font-bold px-2 py-0.5 rounded-md text-[11px] ${
                                        status === "inside"
                                            ? "bg-emerald-100 text-emerald-800"
                                            : status === "outside"
                                            ? "bg-amber-100 text-amber-900"
                                            : "bg-neutral-200 text-neutral-800"
                                    }`}
                                >
                                    {status === "inside"
                                        ? "✓ Inside Pyramid City 3"
                                        : status === "outside"
                                        ? "📍 Outside / Remote"
                                        : status === "denied"
                                        ? "Permission Denied"
                                        : "Locating…"}
                                </span>
                            </div>

                            <div className="flex items-center justify-between text-xs">
                                <span className="text-nest-ink3">Distance to Society:</span>
                                <span className="font-mono font-bold text-nest-ink">
                                    {distanceMeters != null ? `${formatDist(distanceMeters)} away` : "Unknown"}
                                </span>
                            </div>

                            <div className="flex items-center justify-between text-xs">
                                <span className="text-nest-ink3">Perimeter Radius:</span>
                                <span className="font-mono text-nest-ink">{societyConfig.radius_meters} meters</span>
                            </div>

                            <div className="flex items-center justify-between text-xs">
                                <span className="text-nest-ink3">Society Address:</span>
                                <span className="text-nest-ink font-medium text-right text-[11px] max-w-[200px]">
                                    {societyConfig.address}
                                </span>
                            </div>
                        </div>

                        {/* Rule Explanations */}
                        <div className="text-[11px] space-y-1.5 text-nest-ink2 bg-amber-50/60 p-3 rounded-xl border border-amber-200/70">
                            <p className="font-bold text-amber-950">Active Society Policy:</p>
                            <p>• <strong>Guards:</strong> Strictly blocked from logging entries when outside Pyramid City 3.</p>
                            <p>• <strong>Residents:</strong> Allowed remote access from anywhere (to approve visitors and pay dues) with an away notification.</p>
                        </div>

                        {/* Admin Controls */}
                        {isAdmin && (
                            <div className="pt-2 border-t border-nest-line space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-nest-ink flex items-center gap-1.5">
                                        <Settings2 size={13} className="text-nest-clay" />
                                        <span>Admin Calibration</span>
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <label className="text-xs text-nest-ink3">Radius (meters):</label>
                                    <input
                                        type="number"
                                        value={radiusInput}
                                        onChange={(e) => setRadiusInput(e.target.value)}
                                        className="w-24 px-2 py-1 border border-nest-line rounded-lg text-xs font-mono font-bold text-nest-ink"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSaveRadius}
                                        disabled={saving}
                                        className="px-2.5 py-1 text-xs bg-nest-ink text-white rounded-lg font-semibold hover:bg-neutral-800 cursor-pointer"
                                    >
                                        Update
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleCalibrateHere}
                                    disabled={saving || !coords}
                                    className="w-full py-2 px-3 rounded-xl border border-emerald-500 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition"
                                >
                                    <Crosshair size={13} />
                                    <span>Calibrate: Set Current GPS as Society Gate</span>
                                </button>
                            </div>
                        )}

                        <div className="flex items-center gap-2 pt-2">
                            <button
                                type="button"
                                onClick={checkLocation}
                                disabled={checking}
                                className="flex-1 py-2 px-3 rounded-xl bg-nest-alt hover:bg-nest-base text-nest-ink font-semibold text-xs border border-nest-line flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                                {checking ? <Loader2 size={13} className="animate-spin" /> : <Navigation size={13} />}
                                <span>Re-check GPS</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="py-2 px-4 rounded-xl bg-nest-ink hover:bg-neutral-800 text-white font-semibold text-xs cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

